// using copilot-instructions: LogPanel component replaced with a simple HTML table (Time, Severity, Description)
import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import "./PanelBase.css";
import "./LogPanel.css";
import { useLogStore } from "../../utils/logger";
import { PanelToolbar, PanelToolbarHost } from "./PanelToolbar";

const severityBodyTemplate = (severity) => {
  const severityConfig = {
    info: { icon: "pi pi-info-circle", color: "#6366f1", title: "Info" },
    warning: {
      icon: "pi pi-exclamation-triangle",
      color: "#ea580c",
      title: "Warning",
    },
    error: { icon: "pi pi-times-circle", color: "#dc2626", title: "Error" },
  };

  const config = severityConfig[severity] || severityConfig.info;
  return (
    <i
      className={config.icon}
      style={{
        color: config.color,
        fontSize: "14px",
        filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.1))",
      }}
      title={config.title}
    />
  );
};

const LogPanel = ({
  initialToolbarPinned = true,
  onToolbarPinnedChange,
  claimFocus,
}) => {
  const containerRef = useRef(null);
  const logs = useLogStore((state) => state.logs);
  const clearLogs = useLogStore((state) => state.clearLogs);
  const [toolbarPinned, setToolbarPinned] =
    React.useState(initialToolbarPinned);

  useEffect(() => {
    // When Splitter finishes resizing, Workspace dispatches a window 'resize' event.
    // Focus the DataTable scrollable body so wheel/trackpad scrolling works without an extra click.
    const retryTimeouts = [];

    const focusIfScrollable = (root) => {
      if (!root) return false;
      let target = root.querySelector(".p-datatable-scrollable-body");
      if (!target) target = root.querySelector(".log-table-wrapper");
      if (!target) return false;
      // Only focus when there's actual overflow (scrollable)
      if (target.scrollHeight <= target.clientHeight) return false;
      if (!target.hasAttribute("tabindex"))
        target.setAttribute("tabindex", "-1");
      try {
        target.focus({ preventScroll: true });
      } catch {
        // ignore focus errors
      }
      return true;
    };

    const onResize = () => {
      const root = containerRef.current;
      if (!root) return;

      // first attempt immediately
      if (focusIfScrollable(root)) return;

      // retry a few times because PrimeReact may update the DOM after the resize event
      let attempts = 0;
      const tryFocus = () => {
        attempts += 1;
        if (focusIfScrollable(root) || attempts >= 5) return;
        const id = window.setTimeout(tryFocus, 50);
        retryTimeouts.push(id);
      };
      const id = window.setTimeout(tryFocus, 50);
      retryTimeouts.push(id);
    };

    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      // clear any pending retry timeouts
      retryTimeouts.forEach((t) => clearTimeout(t));
    };
  }, []);

  const handleToolbarPinnedChange = React.useCallback(
    (pinned) => {
      setToolbarPinned(pinned);
      onToolbarPinnedChange?.(pinned);
    },
    [onToolbarPinnedChange],
  );

  return (
    <PanelToolbarHost
      pinned={toolbarPinned}
      onMouseDown={claimFocus}
      className="vertical-panel"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        maxHeight: "100%",
        overflow: "hidden",
      }}
      toolbar={
        <PanelToolbar
          pinned={toolbarPinned}
          onPinnedChange={handleToolbarPinnedChange}
          pinDataEvoId="logpanel-btn-toolbar-pin"
          right={
            <Button
              label="Limpar Logs"
              className="btn-vertical-panel"
              onClick={clearLogs}
              data-evo-id="logpanel-btn-clear"
            />
          }
        >
          <h5>Logs</h5>
        </PanelToolbar>
      }
    >
      <div
        className="log-panel-container"
        ref={containerRef}
        style={{
          display: "flex",
          flexDirection: "column",
          flex: "1 1 0",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {logs.length === 0 ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "#6b7280",
              fontSize: "14px",
              textAlign: "center",
              padding: "20px",
            }}
          >
            <i
              className="pi pi-info-circle"
              style={{
                fontSize: "24px",
                marginBottom: "8px",
                color: "#9ca3af",
              }}
            />
            <div>Nenhum log disponível</div>
            <div style={{ fontSize: "12px", marginTop: "4px" }}>
              Os logs aparecerão aqui quando o sistema executar operações
            </div>
          </div>
        ) : (
          <DataTable
            value={logs}
            scrollable
            scrollHeight="flex"
            size="small"
            className="log-datatable"
            stripedRows
            showGridlines
          >
            <Column
              field="time"
              header="Hora"
              style={{ width: "80px", minWidth: "80px" }}
              body={(rowData) => (
                <span
                  style={{
                    fontSize: "12px",
                    color: "#6b7280",
                    fontFamily: "monospace",
                  }}
                >
                  {rowData.time}
                </span>
              )}
            />
            <Column
              header="Nível"
              body={(rowData) => severityBodyTemplate(rowData.level)}
              style={{ width: "60px", minWidth: "60px", textAlign: "center" }}
            />
            <Column
              field="message"
              header="Descrição"
              style={{ minWidth: "200px" }}
              body={(rowData) => (
                <div
                  style={{
                    fontSize: "11px",
                    color: "var(--text-color)",
                    lineHeight: "1.3",
                  }}
                >
                  <div>{rowData.message}</div>
                  {(rowData.details?.friendlyReason ||
                    rowData.details?.reason) && (
                    <div
                      style={{
                        fontSize: "10px",
                        color: "var(--text-color-secondary)",
                        marginTop: "2px",
                        fontStyle: "italic",
                      }}
                    >
                      {rowData.details.friendlyReason || rowData.details.reason}
                    </div>
                  )}
                </div>
              )}
            />
          </DataTable>
        )}
      </div>
    </PanelToolbarHost>
  );
};

LogPanel.propTypes = {
  initialToolbarPinned: PropTypes.bool,
  onToolbarPinnedChange: PropTypes.func,
  claimFocus: PropTypes.func,
};

export default React.memo(LogPanel);
